import { FocusEvent, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchUserByEmail, useCheckUserByEmail, usePostUserReset } from "../../../api/userDetails";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import { ResetPasswordRequest, SupplierUserCheck } from "../../../types/auth.types";
import { AUTH_ROUTES, VALIDATION_MESSAGES } from "../constants/auth.constants";
import { parsePasswordResetState } from "../helpers/passwordResetRoute";
import { ResetPasswordFormValues } from "../types/authForms.types";
import { resetPasswordFormRules } from "../validation/authFormRules";

/**
 * Form state for `PasswordResetNew`. Mirrors the legacy `PasswordReset` flow:
 * `oldPassword`'s label switches on the route's `isPasswordResetRequired`,
 * a supplier (from `useCheckUserByEmail`, triggered on email blur) must
 * accept the terms, and the payload sent to `POST auth/reset-password` is
 * unchanged. `confirmPassword` is new (client-side only, stripped before the
 * request) and the route state is read null-safely — see
 * `parsePasswordResetState`.
 */
export const useResetPasswordViewModel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = useMemo(() => parsePasswordResetState(location.state), [location.state]);

  const methods = useForm<ResetPasswordFormValues>({
    defaultValues: {
      email: routeState.email,
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });
  const { getValues } = methods;

  const [checkEmail, setCheckEmail] = useState(routeState.email);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string>();

  const { data: supplierCheck } = useCheckUserByEmail(checkEmail);
  const isSupplier = supplierCheck?.isSupplier ?? false;

  const [isCheckingSupplier, setIsCheckingSupplier] = useState(false);

  const { mutate, isLoading: isResetting } = usePostUserReset();
  const isPending = isResetting || isCheckingSupplier;

  // Runs after react-hook-form's own blur handling (`FormInput` composes both).
  // Triggers the supplier check early so the terms block shows before submit.
  const onEmailBlur = (event: FocusEvent<HTMLInputElement>) => {
    setCheckEmail(event.target.value);
  };

  const onTermsAcceptedChange = (checked: boolean) => {
    setTermsAccepted(checked);
    if (checked) setTermsError(undefined);
  };

  const onSubmit = methods.handleSubmit(async values => {
    // The blur-triggered check may not have run (autofill never blurs) or may
    // still be in flight, so verify the email actually being submitted. Never
    // assume "not a supplier" when the check fails: that would record terms
    // consent the user was never shown.
    setIsCheckingSupplier(true);
    let check: SupplierUserCheck;
    try {
      check = await fetchUserByEmail(values.email);
    } catch {
      errorView("Unable to verify your account. Please try again.");
      return;
    } finally {
      setIsCheckingSupplier(false);
    }

    const submittingAsSupplier = check?.isSupplier ?? false;
    setCheckEmail(values.email);

    if (submittingAsSupplier && !termsAccepted) {
      setTermsError(VALIDATION_MESSAGES.termsRequired);
      return;
    }

    const payload: ResetPasswordRequest = {
      email: values.email,
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
      isAgreeToTerms: submittingAsSupplier ? termsAccepted : true
    };

    mutate(payload, {
      onSuccess: () => {
        successMessage("Password reset successfully");
        navigate(AUTH_ROUTES.login);
      }
    });
  });

  return {
    isPasswordResetRequired: routeState.isPasswordResetRequired,
    methods,
    onEmailBlur,
    confirmPasswordRule: resetPasswordFormRules.confirmPassword(() => getValues("newPassword")),
    isSupplier,
    supplierTermsHtml: supplierCheck?.terms ?? "",
    termsAccepted,
    onTermsAcceptedChange,
    termsError,
    onSubmit,
    isPending
  };
};
